import React from 'react';
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Gift,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  Users,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { formatDate } from '../adminFormatters';
import GuestMonthsPanel from './GuestMonthsPanel';

const GuestPassesTab = ({
  adminFetch,
  email,
  setEmail,
  name,
  setName,
  sending,
  sentPasses,
  loading,
  handleSendGuestPass,
  fetchSentPasses,
  copyCode,
  isExpired,
  activeZone,
  onOpenTool,
}) => {
  const redeemedCount = sentPasses.filter((pass) => Boolean(pass.redeemed)).length;
  const openPasses = sentPasses.filter((pass) => !pass.redeemed && !isExpired(pass.expires_at));
  const expiredCount = sentPasses.filter((pass) => !pass.redeemed && isExpired(pass.expires_at)).length;
  const followUpCount = openPasses.filter((pass) => {
    if (!pass?.created_at) return false;
    const createdAt = new Date(pass.created_at).getTime();
    return Number.isFinite(createdAt) && (Date.now() - createdAt) >= (3 * 24 * 60 * 60 * 1000);
  }).length;

  return (
    <div className="space-y-6">
      {adminFetch ? <GuestMonthsPanel adminFetch={adminFetch} /> : null}
      <Card className="border-orange-200 bg-gradient-to-br from-[#fff7ee] via-white to-[#f8efe7] p-6 dark:border-orange-500/15 dark:bg-gradient-to-br dark:from-[#241b14] dark:via-[#1c1712] dark:to-[#141312]">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-orange-700 dark:text-orange-300">
              Prospects flow
            </p>
            <h3 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
              Keep invitations tied to a real next move
            </h3>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
              An invitation gives a dad a four-week guest month and ends in a half-hour conversation with you. Send it here, then use Prospects and Email to decide whether the next move should stay personal, wait, or become broader outreach.
            </p>
            {activeZone?.id === 'communication' ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenTool?.('members', 'reach-dads')}
                  className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Review prospects
                  </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenTool?.('email', 'reach-dads')}
                  className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Open next-step outreach
                </Button>
              </div>
            ) : null}
          </div>

          <div className="grid min-w-full gap-3 sm:grid-cols-2 xl:min-w-[360px] xl:max-w-[420px]">
            <div className="rounded-2xl border border-orange-200 bg-white/90 p-4 dark:border-orange-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Open passes</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{openPasses.length}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Invitations still waiting on a response</p>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-white/90 p-4 dark:border-emerald-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Redeemed</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{redeemedCount}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Invitations that became a guest month</p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-white/90 p-4 dark:border-amber-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Follow up soon</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{followUpCount}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Open invitations sitting for more than three days</p>
            </div>
            <div className="rounded-2xl border border-red-200 bg-white/90 p-4 dark:border-red-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Expired</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{expiredCount}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Invitations that likely need a fresh decision instead of a nudge</p>
            </div>
          </div>
        </div>
      </Card>

      <Card className="border border-gray-200 bg-white p-6 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        <h3 className="mb-2 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <Gift className="w-5 h-5 text-aqua" />
          Send Invitation
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          Send a four-week guest month when the next move is a direct invitation, not a full campaign. As admin, you are not capped. The week-four conversation is in the email, so he agrees to it before he accepts.
        </p>

        <form onSubmit={handleSendGuestPass} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-700 dark:text-gray-400 text-sm mb-1 block">Email Address *</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="friend@example.com"
                className="bg-white text-gray-900 border border-gray-300 dark:bg-[#16161a] dark:border-[#2a2a32] dark:text-white"
                required
              />
            </div>
            <div>
              <label className="text-gray-700 dark:text-gray-400 text-sm mb-1 block">Name (optional)</label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                className="bg-white text-gray-900 border border-gray-300 dark:bg-[#16161a] dark:border-[#2a2a32] dark:text-white"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-600 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-300">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-aqua" />
              <p>
                Best for prospects who already know why you are inviting them. If the relationship still needs context, review the prospect record first so the invitation does not feel premature.
              </p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={!email || sending}
            className="bg-aqua hover:bg-aqua-dark text-charcoal"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {sending ? 'Sending...' : 'Send Invitation'}
          </Button>
        </form>
      </Card>

      <Card className="border border-gray-200 bg-white p-6 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Sent Invitations</h3>
          <Button
            type="button"
            variant="outline"
            onClick={fetchSentPasses}
            className="border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:text-white dark:hover:bg-[#16161a]"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 text-aqua animate-spin" />
          </div>
        ) : sentPasses.length === 0 ? (
          <div className="text-gray-600 dark:text-gray-400 text-sm py-6">
            No invitations have been sent yet.
          </div>
        ) : (
          <div className="space-y-3">
            {sentPasses.map((p) => {
              const redeemed = Boolean(p.redeemed);
              const expired = isExpired(p.expires_at) && !redeemed;
              const followUpSoon = !redeemed && !expired && p?.created_at
                ? (Date.now() - new Date(p.created_at).getTime()) >= (3 * 24 * 60 * 60 * 1000)
                : false;

              return (
                <div
                  key={p.pass_id || `${p.recipient_email}-${p.code}`}
                  className="p-4 rounded-xl border border-gray-200 bg-white flex flex-col md:flex-row md:items-center md:justify-between gap-3 dark:border-[#2a2a32] dark:bg-[#16161a]"
                >
                  <div className="space-y-1">
                    <div className="text-gray-900 dark:text-white font-medium">
                      {p.recipient_name || p.recipient_email || 'Unknown recipient'}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400 text-sm">{p.recipient_email || ''}</div>
                    <div className="text-gray-500 dark:text-gray-500 text-xs">
                      {p.created_at ? `Sent: ${formatDate(p.created_at)}` : ''}
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="px-3 py-2 rounded-lg bg-gray-100 text-gray-900 font-mono text-sm border border-gray-300 dark:bg-[#0f0f12] dark:text-white dark:border-[#2a2a32]">
                        {p.code || '—'}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => copyCode(p.code)}
                        disabled={!p.code}
                        className="border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:text-white dark:hover:bg-[#0f0f12]"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Copy
                      </Button>

                      <Badge
                        className={
                          redeemed
                            ? 'bg-green-100 text-green-800 border border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/25'
                            : 'bg-yellow-100 text-yellow-800 border border-yellow-300 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-500/25'
                        }
                      >
                        {redeemed ? 'Accepted' : 'Not accepted'}
                      </Badge>

                      {followUpSoon ? (
                        <Badge className="border border-orange-300 bg-orange-100 text-orange-800 dark:border-orange-500/30 dark:bg-orange-500/15 dark:text-orange-300">
                          Follow up soon
                        </Badge>
                      ) : null}

                      {expired ? (
                        <Badge className="bg-red-100 text-red-800 border border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30">
                          Expired
                        </Badge>
                      ) : null}
                    </div>

                    <div className="text-xs text-gray-400 md:text-right">
                      {p.created_at ? <div>Sent: {formatDate(p.created_at)}</div> : null}
                      {p.expires_at ? <div>Expires: {formatDate(p.expires_at)}</div> : null}
                      {p.redeemed_at ? <div>Redeemed: {formatDate(p.redeemed_at)}</div> : null}
                      {followUpSoon ? (
                        <div className="mt-1 inline-flex items-center gap-1 font-medium text-orange-700 dark:text-orange-300">
                          <ArrowRight className="h-3 w-3" />
                          Consider a personal nudge
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default GuestPassesTab;
